<template>
<div class="editable-container" @click="focus" :style="inputStyle">
  <div
      ref="editableDiv"
      inputmode="numeric"
      contenteditable="true" role="textbox"
      @input="onInput"
      @focus="focus"
      @blur="unfocus"
      @keydown="onKeyDown"
      :style="inputStyle"
      :id="elementId + '-timeInput'"
      class="
     editable-time
     model-number-input"
      v-html="timeVal">
  </div>
  <div class="timePlaceholder" ref="timePlaceholder" contenteditable="true" v-html="placeholderFormattedValue">
  </div>
</div>
</template>

<script setup lang="ts">
import 'primevue/resources/themes/saga-blue/theme.css'
import 'primevue/resources/primevue.min.css'
import {computed, nextTick, onMounted, ref, toRefs, watch} from 'vue'
import {storeToRefs} from "pinia";
import { useWidgetStore} from "@/store";
import {deleteDiscardedDB} from "@/utils/database";

const widgetStore = useWidgetStore()
const {inputSelected} = storeToRefs(widgetStore)

const isFocused = ref(false)

const timePlaceholder = ref()

const props = defineProps({
  modelValue: {
    required: true
  },
  placeholder: {
    type: Number,
    required: true
  },
  fontSize: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    required: true
  },
  savedTimeFormat: {
    type: String,
    required: true
  },
  inputStyle: {
    type: Object,
    required: true
  },
  elementData: {
    type: Object
  },
})

const emit = defineEmits(['update:modelValue']);

const displayInput = ref('none')
const displaytimePlaceholder = ref('none')

onMounted(() => {
  if (props.modelValue !== undefined && props.modelValue !== null) {
    displayInput.value = 'flex'
    displaytimePlaceholder.value = 'none'
  }
  else {
    displayInput.value = 'none'
    displaytimePlaceholder.value = 'flex'
  }
})

const {elementData} = toRefs(props)
const elementId = computed(() => elementData.value?.id ?? null)



watch(props, () => {
  tempFormat.value = ''

  if (isFocused.value || (props.modelValue !== undefined && props.modelValue !== null)) {
    displayInput.value = 'flex'
    displaytimePlaceholder.value = 'none'
  }
  else {
    displayInput.value = 'none'
    displaytimePlaceholder.value = 'flex'
  }
}, { deep: true })

const fontSizeSeconds = computed(() => {
  return `${Math.floor(parseInt(props.fontSize) * 0.85)}px`;
})

const marginSeconds = computed(() => {
  return `${Math.floor(parseInt(props.fontSize) - parseInt(fontSizeSeconds.value))}px`;
})


const fontSizePx = computed(() => {
  return `${props.fontSize}px`;
})

const editableDiv = ref()
const tempFormat = ref('')

const placeholderFormattedValue = computed(() => {

  let timeInSeconds

  if (props.savedTimeFormat === 'Hours') {
    timeInSeconds = props.placeholder * 3600.0
  }
  else if (props.savedTimeFormat === 'Minutes') {
    timeInSeconds = props.placeholder * 60.0
  }
  else {
    timeInSeconds = props.placeholder
  }

  const hours = Math.floor(timeInSeconds / 3600.0)
  const remainingMinutes = (timeInSeconds / 60.0) % 60
  const minutes = Math.floor(remainingMinutes)
  const seconds = Math.round((remainingMinutes - minutes) * 60.0)

  return formatTime({ hours, minutes, seconds})
})


const formatTime = (time) => {

  const { hours, minutes, seconds } = time

  switch (props.timeFormat) {
    case 'HH:mm ss': {
      return `${pad(hours, 2)}:${pad(minutes, 2)}&nbsp;<span class="small">${pad(seconds, 2)}</span>`
    }

    case 'H:mm ss': {
      return `${hours}:${pad(minutes, 2)}&nbsp;<span class="small">${pad(seconds, 2)}</span>`
    }

    case 'HH:mm': {
      return `${pad(hours,2)}:${pad(minutes, 2)}`
    }

    case 'H:mm': {
      return `${hours}:${pad(minutes, 2)}`
    }

    case 'mm ss': {
      if (hours > 0) {
        return `${hours}:${pad(minutes, 2)}&nbsp;<span class="small">${pad(seconds, 2)}</span>`
      }
      else {
        return `${pad(minutes, 2)}&nbsp;<span class="small">${pad(seconds, 2)}</span>`
      }
    }

    case 'm ss': {
      if (hours > 0) {
        return `${hours}:${pad(minutes, 2)}&nbsp;<span class="small">${pad(seconds, 2)}</span>`
      }
      else {
        return `${minutes}&nbsp;<span class="small">${pad(seconds, 2)}</span>`
      }
    }

    default:
      return '';
  }

}

const heightPx = computed(() => props.inputStyle.height)

const pad = (number, length) => {
  let sNumber = String(number)
  return sNumber.padStart(length, '0')
}

const parseIntEmpty = (val) => {
  if (val === undefined || val === null || val.length === 0) {
    return 0
  }
  return parseInt(val)
}

const parseTime = (time) => {
  const result = parseTimeAsStrings(time)
  return {
    hours: parseIntEmpty(result.hours),
    minutes: parseIntEmpty(result.minutes),
    seconds: parseIntEmpty(result.seconds)
  }

}

const parseTimeAsStrings = (time) => {
  // make sure only digits, spaces and colon
  time = time.replace(/[^0-9: ]/g, '')

  if (props.timeFormat === 'HH:mm' || props.timeFormat === 'H:mm') {
    const parts = time.split(':')
    if (parts.length > 1) {
      const hours = parts[0]
      const minutes = parts[1]
      return {hours, minutes, seconds: '0'}
    }
    else {
      const minutes = time
      return {hours: '0', minutes, seconds: '0'}
    }
  }
  else {
    const parts = time.split(':')
    let hours = 0
    let minSecParts

    if (parts.length > 1) {
      hours = parts[0]
      minSecParts = parts[1].split(' ')
    }
    else {
      minSecParts = time.split(' ').filter(entry => entry !== '')
    }

    let minutes = 0
    let seconds

    if (minSecParts.length > 1) {
      minutes = minSecParts[0]
      seconds = minSecParts[1]
    }
    else {
      seconds = minSecParts[0]
    }

    return { hours, minutes, seconds }
  }
}



const timeVal = computed({
  get() {
    // tempFormat helps when like minutes > 60 to preserve typing
    if (isFocused.value === true && tempFormat.value.length > 0) {
      return tempFormat.value
    }

    let timeInSeconds

    if (props.savedTimeFormat === 'Hours') {
      timeInSeconds = props.modelValue * 3600.0
    }
    else if (props.savedTimeFormat === 'Minutes') {
      timeInSeconds = props.modelValue * 60.0
    }
    else {
      timeInSeconds = props.modelValue
    }

    const hours = Math.floor(timeInSeconds / 3600.0)
    const remainingMinutes = (timeInSeconds / 60.0) % 60
    const minutes = Math.floor(remainingMinutes)
    const seconds = Math.round((remainingMinutes - minutes) * 60.0)

    return formatTime({ hours, minutes, seconds})
  },

  set(val) {
    const { hours, minutes, seconds } = parseTime(val)
    const srcSeconds = hours * 3600 + minutes * 60 + seconds
    let converted = srcSeconds

    if (props.savedTimeFormat === 'Hours') {
      converted = srcSeconds / 3600.0
    }
    else if (props.savedTimeFormat === 'Minutes') {
      converted = srcSeconds / 60.0
    }

    emit('update:modelValue', converted);
  }
});

const unfocus = (event) => {
  isFocused.value = false

  if (tempFormat.value.length === 0) {
    return
  }

  const { hours, minutes, seconds } = parseTime(tempFormat.value)
  const srcSeconds = hours * 3600 + minutes * 60 + seconds
  let converted = srcSeconds

  if (props.savedTimeFormat === 'Hours') {
    converted = srcSeconds / 3600.0
  }
  else if (props.savedTimeFormat === 'Minutes') {
    converted = srcSeconds / 60.0
  }

  console.log('converting', props.savedTimeFormat, srcSeconds, converted)
  emit('update:modelValue', converted);
}

const focus = () => {

  isFocused.value = true
  displayInput.value = 'flex'
  displaytimePlaceholder.value = 'none'
  tempFormat.value = timeVal.value
  editableDiv.value.focus()

  nextTick(() => {
    editableDiv.value.focus()
    setTimeout(() => {
      editableDiv.value.focus();
      document.execCommand('selectAll', false, null);
    }, 100)
  });
}


const moveCursorToEnd = () => {
  editableDiv.value.focus();
  document.execCommand('selectAll', false, null);
  document.getSelection().collapseToEnd();

};


const onInput = (event) => {

  let rawValue = event.target.textContent
  let sTime = rawValue.replace(/[^0-9]/g, '')

  const seconds = parseIntEmpty(sTime.slice(-2))

  sTime = sTime.slice(0, -2) || '0'
  const minutes = parseIntEmpty(sTime.slice(-2))
  const hours = parseIntEmpty(sTime.slice(0, -2) || '0')

  tempFormat.value = formatTime({ hours, minutes, seconds })

  setTimeout(() => {
    moveCursorToEnd()
  }, 0)

}
const onKeyDown = (event) => {
  // Allow numbers (0-9), backspace, and tab
  const allowedKeys = ["Backspace", "Tab"];
  if (
      !allowedKeys.includes(event.key) &&
      (event.key < "0" || event.key > "9")
  ) {
    event.preventDefault();
  }

  if (event.key === "Enter") {
    if (document['focusNextElement']) {
      document['focusNextElement']()
    }
  }
}
</script>
<style lang="scss" scoped>

.editable-container {
  height: v-bind(heightPx) !important;
  position: relative;
  width: 300px;
}


.editable-time {
  padding: 0.5rem;
  height: v-bind(heightPx) !important;
  display: v-bind(displayInput);
  font-size: v-bind(fontSize);
  align-items: center;
  justify-content: right;
}

.timePlaceholder {
  height: v-bind(heightPx) !important;
  display: v-bind(displaytimePlaceholder);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  font-size: v-bind(fontSize);
  color: red;
  padding-right: 10px;
  background-color: transparent;
  pointer-events: none;
  opacity: 1;
  align-items: center;
  justify-content: right;
}

:deep(.timePlaceholder input:hover) {
  opacity: 0.8;
}

:deep(.small) {
  font-size: v-bind(fontSizeSeconds);
  position: relative;
  margin-top: v-bind(marginSeconds);
}

.editable-time {
  width: 100% !important;
}

[contenteditable]:focus {
  outline: none;
}



</style>
